import { reaction } from 'mobx'

import type { SessionStorageDataT } from '@/constant/sessionStorageData'
import { sessionStorageService } from '@/service/storage.service'

type MapEntriesKey = {
  [K in keyof SessionStorageDataT]: SessionStorageDataT[K] extends [string | number, string][]
    ? K
    : never
}[keyof SessionStorageDataT]

// 배포를 넘나드는 새로고침에서는 이전 버전이 저장한 값을 읽게 되므로, JSON 은 유효해도
// 스키마가 다를 수 있다 — 엔트리 형태까지 확인하고 어긋나면 복원하지 않는다.
const isValidEntries = (stored: unknown): stored is [string | number, string][] =>
  Array.isArray(stored) &&
  stored.every(
    (entry) =>
      Array.isArray(entry) &&
      entry.length === 2 &&
      (typeof entry[0] === 'string' || typeof entry[0] === 'number') &&
      typeof entry[1] === 'string',
  )

// 접근 불가 환경(사파리 프라이빗 등)에서는 조용히 세션 메모리로만 동작한다.
// reaction 은 앱 수명 내내 유지되는 서비스에서 쓰는 전제로 dispose 하지 않는다.
export const syncMapWithSessionStorage = <Key extends string | number>(
  key: MapEntriesKey,
  getMap: () => Map<Key, string>,
  restore: (restored: Map<Key, string>) => void,
) => {
  try {
    const stored = sessionStorageService.getItem(key)
    if (isValidEntries(stored)) restore(new Map(stored as [Key, string][]))
  } catch {
    // 읽기 불가 — 빈 상태로 시작
  }

  reaction(
    () => Array.from(getMap().entries()),
    (entries) => {
      try {
        sessionStorageService.setItem(key)(entries)
      } catch {
        // 저장 불가 — 이번 세션 메모리로만 동작
      }
    },
  )
}

type StringValueKey = {
  [K in keyof SessionStorageDataT]: SessionStorageDataT[K] extends string ? K : never
}[keyof SessionStorageDataT]

// 동작·전제는 syncMapWithSessionStorage 와 동일하다.
export const syncValueWithSessionStorage = (
  key: StringValueKey,
  getValue: () => string | undefined,
  restore: (restored: string) => void,
) => {
  try {
    const stored = sessionStorageService.getItem(key)
    if (typeof stored === 'string') restore(stored)
  } catch {
    // 읽기 불가 — 빈 상태로 시작
  }

  reaction(
    () => getValue(),
    (value) => {
      if (value === undefined) return
      try {
        sessionStorageService.setItem(key)(value)
      } catch {
        // 저장 불가 — 이번 세션 메모리로만 동작
      }
    },
  )
}
